import AddIcon from '@mui/icons-material/Add';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Resizer from 'react-image-file-resizer';
import { connect } from 'react-redux';
import photo from '../../assets/images/homeImage.png';
import {
    CREATE_EVENT,
    SET_USER_DATA,
    UPDATE_DRAFT_EVENT,
    UPLOAD_CANCEL,
    UPLOAD_VIDEO
} from '../../constants/actionTypes';
import styles from './createVideo.module.scss';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import { isMobile } from 'react-device-detect';
import { getBase64 } from '../../helpers/FileReader';
import fetchData from '../../fetch';
import sanity from '../../sanity';
import { transformToSanityUrl } from '../../utils';
import { ActionIcon, TextField, UploadMediaButton } from '../../core/components';
import videoUploadedIconSrc from './assets/video-uploaded.svg';
import trailerUploadedIconSrc from './assets/trailer-uploaded.svg';
import thumbnailUploadedIconSrc from './assets/thumbnail-uploaded.svg';
import addVideoIconSrc from './assets/add-video.svg';
import addTrailerIconSrc from './assets/add-trailer.svg';
import addThumbnailIconSrc from './assets/add-thumbnail.svg';

const mapStateToProps = (state) => {
    return {
        ...state.liveStreaming,
        user: state.user.currentUser,
        upload: state.common.upload,
        draftEvent: state.common.draftEvent,
        currency: state.common.currency,
        connected_account: state.common.connected_account
    };
};
const mapDispatchToProps = (dispatch) => ({
    onNewEvent: (payload) => dispatch({ type: CREATE_EVENT, payload }),
    onUploadVideo: (payload) => dispatch({ type: UPLOAD_VIDEO, payload }),
    onSetUserData: (payload) => dispatch({ type: SET_USER_DATA, payload }),
    onUpdateDraftEvent: (payload) => dispatch({ type: UPDATE_DRAFT_EVENT, payload }),
    onUploadCancel: (payload) => dispatch({ type: UPLOAD_CANCEL, payload })
});

const UploadContent = (props) => {
    const { t } = useTranslation();
    const [trailerFile, setTrailerFile] = useState(props.draftEvent.trailerFile);
    const [videoFileType, setVideoFileType] = useState(props.draftEvent.videoFileType);
    const [trailerFileType, setTrailerFileType] = useState(props.draftEvent.trailerFileType);
    const [endDateFieldEnabled, setEndDateFieldEnabled] = useState(false);
    const upload = props.upload.find((x) => x.jobId === props.jobId);
    const [imagePreview, setImagePreview] = useState(props.draftEvent.imagePreview);
    const [loading, setLoading] = useState(false);

    const onDrop = (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            handleVideoUpload({ target: { files: [file] } }, 'video');
        }
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: { 'video/*': [] },
        multiple: false
    });

    const calculateMinimumPrice = () => {
        return Math.round(3 * props.currency.rate * 100) / 100;
    };

    const getVideoDuration = async (video) => {
        try {
            props.setVideoDuration(null);

            if (!video?.type?.includes('video/')) {
                setEndDateFieldEnabled(true);
                return;
            }

            setEndDateFieldEnabled(false);

            const fileReader = new FileReader();

            fileReader.onload = function (e) {
                try {
                    const arrayBuffer = e.target.result;

                    // Ensure blob creation is safe
                    const blob = new Blob([arrayBuffer], { type: video.type });
                    const url = URL.createObjectURL(blob);

                    const videoDom = document.createElement('video');
                    videoDom.preload = 'metadata';

                    videoDom.onloadedmetadata = () => {
                        try {
                            props.setVideoDuration(videoDom.duration);
                            const isPortrait = videoDom.videoHeight > videoDom.videoWidth;
                            props.setVideoOrientation(isPortrait ? 'portrait' : 'landscape');
                        } catch (metaErr) {
                            console.error('Error reading video metadata:', metaErr);
                        } finally {
                            URL.revokeObjectURL(url); // Cleanup URL
                        }
                    };

                    videoDom.onerror = () => {
                        console.error('Error loading video metadata');
                        setEndDateFieldEnabled(true);
                        URL.revokeObjectURL(url); // Cleanup on error
                    };

                    videoDom.src = url; // Set the video source last to minimize potential race conditions
                } catch (processingErr) {
                    console.error('Error processing video file:', processingErr);
                    setEndDateFieldEnabled(true);
                }
            };

            fileReader.onerror = (err) => {
                console.error('FileReader error:', err);
                setEndDateFieldEnabled(true);
            };

            fileReader.readAsArrayBuffer(video);
        } catch (err) {
            console.warn('Unhandled error:', err);
            setEndDateFieldEnabled(true);
        }
    };

    const handleVideoUpload = async (event, type) => {
        props.setFileUploadError('');
        const allowedVideoExtensions = ['mp4', 'mov', 'avi', 'mkv', 'flv', 'wmv', 'webm'];

        const file = event?.target?.files[0];
        const fileExtension = file?.name.split('.').pop().toLowerCase();
        if (!allowedVideoExtensions.includes(fileExtension)) {
            toast.error(t('PleaseUploadVideo'));
            return;
        }
        if (type === 'trailer') {
            setTrailerFile(file);
            const type = file && file?.name ? file.name.toLowerCase().split('.').pop() : null;
            setTrailerFileType(type);
            props.setHasTrailer(true);
            props.onUpdateDraftEvent({
                trailerFile: file.name,
                hasTrailer: true,
                trailerFileType: type
            });

            props.onUploadVideo({
                file,
                type,
                jobId: props.jobId,
                trailer: true
            });
        }
        if (type === 'video') {
            props.setHasTrailer(false);
            props.setFileUploadError('');
            props.setFileUpload(true);
            const type = file && file?.name ? file.name.toLowerCase().split('.').pop() : null;
            props.setVideoFile(file);
            setVideoFileType(type);
            props.onUpdateDraftEvent({
                videoFile: file.name,
                videoFileType: type,
                fileUpload: true
            });
            const minPrice = calculateMinimumPrice();
            props.setMinimumPrice(minPrice);
            props.onUpdateDraftEvent({ minimumPrice: minPrice });

            props.onUploadVideo({ file, type, jobId: props.jobId });
            //TO SOLVE getVideoDuration creates crashes in iOS
            // if (event.target.files[0]) {
            //     await getVideoDuration(event.target.files[0]);
            // }
        }
    };

    const resizeFile = (file) => {
        return new Promise((resolve) => {
            Resizer.imageFileResizer(
                file,
                1920,
                1080,
                'PNG',
                100,
                0,
                (uri) => {
                    resolve(uri);
                },
                'blob'
            );
        });
    };

    const handleImageChange = async (e) => {
        e?.preventDefault?.();
        if (!e.target.files[0]) {
            return;
        }
        let reader = new FileReader();
        let currentFile = e.target.files[0];
        const allowedExtensions = ['jpg', 'jpeg', 'png'];
        const fileExtension = currentFile.name.split('.').pop().toLowerCase();

        if (!allowedExtensions.includes(fileExtension)) {
            // presentToast(
            //     'Please select a valid image file (jpg, jpeg, png).',
            //     closeCircleSharp,
            //     'danger',
            //     1500
            // );
            toast.error(t('PleaseSelectValidImage'));
            return;
        }

        reader.onloadend = () => {
            setImagePreview(reader.result);
            props.onUpdateDraftEvent({ imagePreview: reader.result });
        };

        try {
            if (currentFile) {
                const image = await resizeFile(currentFile);
                const base64Image = await getBase64(image);
                const data = base64Image.split(';base64,')[1];
                const requestOptions = {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        img: {
                            data,
                            name: new Date().getTime()
                        }
                    })
                };
                setLoading(true);
                const res = await fetchData.Event.UploadThumbnail(requestOptions);
                setLoading(false);
                const _image = sanity.urlFor(res.image_asset.asset);
                const transformedImage = transformToSanityUrl(_image.options);

                const thumbnail = { _ref: res._id };
                props.setThumbnail(thumbnail);
                props.onUpdateDraftEvent({ thumbnail, imageUrl: transformedImage });
                reader.readAsDataURL(image);
            }
        } catch (err) {
            console.error('ERROR with thumbnail: ', err);
        }
    };

    const deleteVideo = () => {
        props.setFileUpload(false);
        props.setVideoFile(null);
        props.setVideoDuration(null);
        setVideoFileType(null);
        props.onUpdateDraftEvent({ videoFile: null, videoFileType: null, fileUpload: false });
        props.onUploadCancel({ jobId: props.jobId });
    };

    const deleteTrailer = () => {
        setTrailerFile(null);
        setTrailerFileType(null);
        props.onUpdateDraftEvent({ trailerFile: null, trailerFileType: null });
        props.onUploadCancel({ jobId: props.jobId, trailer: true });
    };

    const handleImagePreviewDelete = () => {
        setImagePreview(null);
        props.onUpdateDraftEvent({ imagePreview: null });
    };

    const hasFilesUploaded = !!props.videoFile || !!imagePreview || !!trailerFile;

    const isVideoUploading =
        (videoFileType && !upload?.upload_url && !upload?.upload_progress) || !!upload?.upload_show;

    const isTrailerUploading =
        (trailerFileType && !upload?.trailer_url && !upload?.trailer_progress) ||
        !!upload?.trailer_show;

    return (
        <>
            <Typography fontSize={'28px'} sx={{ lineHeight: '100%', letterSpacing: '-0.56px' }}>
                {t('UploadExplanation')}
            </Typography>
            <Box className={styles.uploadContent}>
                <div className={styles.uploadContentHead}>
                    {!hasFilesUploaded && (
                        <Box {...getRootProps()} className={styles.uploadVideo}>
                            <ActionIcon
                                Icon={AddIcon}
                                title={t('UploadContent')}
                                subtitle={t('UploadLimit')}
                                hasError={!!props.fileUploadError}
                            />
                            <input {...getInputProps()} />
                        </Box>
                    )}
                    {hasFilesUploaded && (
                        <Box className={styles.uploadInputs}>
                            <UploadMediaButton
                                placeholderIconSrc={addVideoIconSrc}
                                placeholderLabel={t('UploadContent')}
                                loading={isVideoUploading}
                                loadingValue={upload?.upload_progress}
                                onCancelClick={() => deleteVideo()}
                                onChange={(e) => handleVideoUpload(e, 'video')}
                                accept="video/*"
                                hasError={!!props.fileUploadError}
                                uploaded={!!props.videoFile}
                                uploadedIconSrc={videoUploadedIconSrc}
                                uploadedLabel={t('Video')}
                            />
                            <UploadMediaButton
                                placeholderIconSrc={addThumbnailIconSrc}
                                placeholderLabel={t('AddThumbnail')}
                                onCancelClick={() => handleImagePreviewDelete()}
                                loading={loading}
                                onChange={handleImageChange}
                                accept="image/*"
                                uploaded={!!imagePreview}
                                uploadedIconSrc={thumbnailUploadedIconSrc}
                                uploadedLabel={t('Thumbnail')}
                            />
                            <UploadMediaButton
                                placeholderIconSrc={addTrailerIconSrc}
                                placeholderLabel={t('AddTrailer')}
                                loading={isTrailerUploading}
                                loadingValue={upload?.trailer_progress}
                                onCancelClick={() => deleteTrailer()}
                                onChange={(e) => handleVideoUpload(e, 'trailer')}
                                accept="video/*"
                                uploaded={!!trailerFile}
                                uploadedIconSrc={trailerUploadedIconSrc}
                                uploadedLabel={t('Trailer')}
                            />
                        </Box>
                    )}
                    {props.fileUploadError && (
                        <Typography fontSize={isMobile ? '14px' : '16px'} color={'red'}>
                            {props.fileUploadError}
                        </Typography>
                    )}
                </div>
                <Box className={styles.uploadFields}>
                    <TextField
                        label={t('AddTitle')}
                        value={props.title}
                        error={props.titleError}
                        onChange={(e) => {
                            props.setTitle(e.target.value);
                        }}
                        onFocus={() => {
                            props.setTitleError('');
                        }}
                        onBlur={(e) => {
                            props.onUpdateDraftEvent({ title: e.target.value });
                        }}
                        placeholder={t('TitlePlaceholder')}
                        fullWidth
                    />

                    <TextField
                        label={t('AddDescription')}
                        value={props.description}
                        onChange={(e) => props.setDescription(e.target.value)}
                        onFocus={() => {
                            props.setDescriptionError('');
                        }}
                        error={props.descriptionError}
                        onBlur={(e) => {
                            props.onUpdateDraftEvent({ description: e.target.value });
                        }}
                        placeholder={t('DescriptionPlaceholder')}
                        fullWidth
                    />
                </Box>
            </Box>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadContent);
